import type { FunctionComponent, HTMLProps, ReactNode } from 'react'
import { createElement } from 'react'

import classnames from 'classnames'

import styles from './GridItem.module.scss'

export const GridItem: FunctionComponent<Props> = ({
  children,
  className,
  tag = 'div',
  columnStart = {},
  span,
  ...rest
}) => {
  const { small, medium, large, xlarge } = span || {}

  const smallBPStartColumn = columnStart.small || 1
  const smallBPEndColumn = smallBPStartColumn + (Number(small) || 2)

  const mediumBPStartColumn = columnStart.medium || 1
  const mediumBPEndColumn = mediumBPStartColumn + (Number(medium) || 12)

  const largeBPStartColumn = columnStart.large || 1
  const largeBPEndColumn = largeBPStartColumn + (Number(large) || 12)

  const xlargeBPStartColumn = columnStart.xlarge || 1
  const xlargeBPEndColumn = xlargeBPStartColumn + (Number(xlarge) || 12)

  // xlarge breakpoint is mobile-first, not an exclusive range, so these classes are only applied if xlarge prop is provided
  const xlargeClasses =
    xlarge || columnStart.xlarge
      ? [
          styles['full-bleed-override'],
          styles[
            xlarge === 'full-bleed'
              ? 'full-bleed-xlarge'
              : `start-xlarge-${xlargeBPStartColumn}`
          ],
          styles[
            xlarge === 'full-bleed' ? '' : `end-xlarge-${xlargeBPEndColumn}`
          ],
        ]
      : []

  const classList = classnames(
    styles[
      small === 'full-bleed'
        ? 'full-bleed-small'
        : `start-small-${smallBPStartColumn}`
    ],
    styles[
      medium === 'full-bleed'
        ? 'full-bleed-medium'
        : `start-medium-${mediumBPStartColumn}`
    ],
    styles[
      large === 'full-bleed'
        ? 'full-bleed-large'
        : `start-large-${largeBPStartColumn}`
    ],
    styles[small === 'full-bleed' ? '' : `end-small-${smallBPEndColumn}`],
    styles[medium === 'full-bleed' ? '' : `end-medium-${mediumBPEndColumn}`],
    styles[large === 'full-bleed' ? '' : `end-large-${largeBPEndColumn}`],
    ...xlargeClasses
  )

  const props = {
    className: classnames(classList, className, styles.gridItem),
    ...rest,
  }

  return createElement(tag, props, children)
}

///////// IMPLEMENTATION /////////

type Props = {
  /**
   * Provide content for the GridItem component
   */
  children: ReactNode
  /**
   * Class name that can be added for additional styling
   */
  className?: string
  /**
   * Details of column spans for each breakpoint. When span and columnStart are not provided, the whole grid is spanned. Note: when xlarge is not provided, value for large is used for xlarge breakpoint.
   */
  span?: {
    small?: ColSpanSm
    medium?: ColSpan
    large?: ColSpan
    xlarge?: ColSpan
  }
  /**
   * Details of start columns for each breakpoint. Note: when xlarge is not provided, the large value is used for the xlarge breakpoint.
   */
  columnStart?: {
    small?: ColNumbersSm
    medium?: ColNumbers
    large?: ColNumbers
    xlarge?: ColNumbers
  }
  tag?: 'div' | 'section'
} & Omit<HTMLProps<HTMLElement>, 'span' | 'columnStart'>

export type ColNumbersSm = 1 | 2

export type ColNumbers = ColNumbersSm | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12

export type ColSpanSm = ColNumbersSm | 'full-bleed'

export type ColSpan = ColNumbers | 'full-bleed'
